import type { FlightCardData } from '@massilia/queries'
import type { ReactNode } from 'react'

import { useInstallmentPartner } from './use-installment-partner'

interface InstallmentMentionProps {
  flight: FlightCardData
  /**
   * `full` : logo et explication sur deux lignes, pour la section vol du desktop.
   * `compact` : une ligne, pour la barre de prix du mobile.
   */
  variant?: 'compact' | 'full'
}

/**
 * Mention « paiement en plusieurs fois » de la carte de vol.
 *
 * Volontairement sans chiffre : le nombre d'échéances dépend du financeur —
 * Massilia va jusqu'à 10x quand FLOA et Oney plafonnent à 3 ou 4x — et une
 * pastille « 3/4x » affichée sur toutes les offres serait fausse pour la
 * moitié d'entre elles. Le logo identifie le financeur, le texte reste vrai
 * dans les deux cas.
 */
export function InstallmentMention({ flight, variant = 'compact' }: InstallmentMentionProps) {
  const partner = useInstallmentPartner()
  const isMassiliaOffer = flight.isInstallmentEligibleAtSearch

  const logoClassName = variant === 'full' ? 'h-5 w-auto' : 'h-3.5 w-auto'

  const logo: ReactNode = isMassiliaOffer ? (
    <img alt="Massilia Voyages" className={logoClassName} src="/logos/massilia-logo.png" />
  ) : (
    <img alt={partner.name} className={logoClassName} src={partner.logoSrc} />
  )

  if (variant === 'full') {
    return (
      <span className="flex items-center gap-2 text-gray-700">
        {logo}
        <span className="text-xs leading-tight">
          <span className="block font-medium">Paiement en plusieurs fois</span>
          {isMassiliaOffer ? (
            <span className="block">
              sans frais <span className="font-bold">Massilia Voyages</span>
            </span>
          ) : (
            <span className="block">
              avec <span className="font-bold">{partner.name}</span>
            </span>
          )}
        </span>
      </span>
    )
  }

  return (
    <span className="flex flex-wrap items-center gap-x-1.5 text-gray-600">
      {logo}
      Paiement en plusieurs fois
      {isMassiliaOffer && <span className="font-semibold">sans frais</span>}
    </span>
  )
}
